/**
 * Page Panel
 *
 * Panel for static content and server-rendered HTML.
 *
 * @author Adam Tran <aviettran@gmail.com>
 * @license MIT
 */

import preact from "../js/lib/preact";
import { PS, PSRoom, type RoomOptions } from "./client-main";
import { PSPanelWrapper, PSRoomPanel } from "./panels";
import { BattleLog } from "./battle-log";
import type { Args } from "./battle-text-parser";

export class SanitizedHTML extends preact.Component<{ children: string }> {
	override componentDidMount() {
		this.updateHTML(this.props.children);
	}
	override shouldComponentUpdate(nextProps: { children: string }) {
		if (nextProps.children !== this.props.children) this.updateHTML(nextProps.children);
		return false;
	}
	updateHTML(html: string) {
		this.base!.innerHTML = BattleLog.sanitizeHTML(html);
		BFArcadeMount(this.base!); // BF-ARCADE-BOARD-MOUNT
		BFArcadeIcons(this.base!); // BF-ARCADE-ICONS-MOUNT
	}
	override render() {
		return <div></div>;
	}
}

// BF-ARCADE-BOARD-FN: the Battle Arcade game board's local spin loop.
// Cadence and the 30-second stop timer follow the cartridge's frame
// table at 30fps (sCursorMoveFrames, battle_arcade/main.c); randomize
// mode replays the sidecar's pre-rolled teleport sequence.
function BFArcadeMount(base: HTMLElement) {
	const el = base.querySelector('div[data-bfarcade]') as HTMLElement | null;
	if (!el || (el as any).bfMounted) return;
	(el as any).bfMounted = true;
	let spin: any;
	try { spin = JSON.parse(el.getAttribute('data-bfarcade')!); } catch { return; }
	const cells = el.querySelectorAll('td');
	const btn = el.querySelector('button') as HTMLButtonElement | null;
	if (cells.length !== 16 || !btn) return;
	const fps = spin.fps || 30;
	const msPerHop = (spin.framesPerHop || 4) * 1000 / fps;
	const totalMs = (spin.timerFrames || 900) * 1000 / fps;
	const seq: number[] = spin.hopSeq || [];
	let hops = 0;
	let pos = spin.startPos || 0;
	// BF-ARCADE-CURSOR: the cartridge's cursor sprite rides the hops
	// (appendChild MOVES the img; BFArcadeIcons may wipe a cell's
	// children once at mount — the next paint re-appends it)
	const cursor = document.createElement('img');
	cursor.src = '/sprites/bfarcade/35.png';
	cursor.alt = '';
	cursor.style.cssText = 'position:absolute;left:-8px;top:-8px;width:80px;height:80px;image-rendering:pixelated;pointer-events:none;z-index:1';
	cells.forEach(c => { (c as HTMLElement).style.position = 'relative'; });
	const paint = () => {
		cells[pos]?.appendChild(cursor);
		btn.setAttribute('value', `/bfarcadestop ${spin.formatid},${hops}`);
	};
	const note = el.querySelector('p');
	if (note) note.textContent = 'Hit Stop (or press Space) to take the panel under the cursor!'; // BF-ARCADE-SPACE
	const onKey = (ev: KeyboardEvent) => {
		if (ev.code !== 'Space' && ev.key !== ' ') return;
		if (!el.isConnected) { document.removeEventListener('keydown', onKey, true); return; }
		const t = ev.target as HTMLElement | null;
		if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
		ev.preventDefault();
		ev.stopPropagation();
		document.removeEventListener('keydown', onKey, true);
		btn.click();
	};
	document.addEventListener('keydown', onKey, true);
	paint();
	const start = Date.now();
	const iv = setInterval(() => {
		if (!el.isConnected) { clearInterval(iv); return; }
		const elapsed = Date.now() - start;
		const want = Math.min(Math.floor(elapsed / msPerHop), spin.totalHops ?? 9999);
		while (hops < want) {
			hops++;
			pos = spin.randomize ? (seq[hops - 1] ?? pos) : (pos + 1) % 16;
		}
		paint();
		if (elapsed >= totalMs) {
			clearInterval(iv);
			btn.click(); // the 30-second timer stops the board
		}
	}, Math.max(msPerHop / 2, 8));
}

// BF-ARCADE-ICONS-FN: swap the game board's text cells for the real
// cartridge panel art (32x32 sprites, anim id == effect id — see
// tools/extract_arcade_icons.py). Runs on both stages; the landed
// panel keeps its gold highlight.
function BFArcadeIcons(base: HTMLElement) {
	const el = base.querySelector('div[data-bfarcadeicons]') as HTMLElement | null;
	if (!el || (el as any).bfIconed) return;
	(el as any).bfIconed = true;
	let info: any;
	try { info = JSON.parse(el.getAttribute('data-bfarcadeicons')!); } catch { return; }
	const cells = el.querySelectorAll('td');
	if (cells.length !== 16 || !info.board) return;
	cells.forEach((td, i) => {
		const cell = info.board[i];
		if (!cell) return;
		const t = td as HTMLElement;
		const img = document.createElement('img');
		img.src = `/sprites/bfarcade/${Number(cell.e)}.png`;
		img.alt = img.title = String(cell.name || '');
		img.width = 64;
		img.height = 64;
		img.style.imageRendering = 'pixelated';
		img.style.display = 'block';
		img.onerror = () => { t.textContent = String(cell.name || '?'); }; // keep the text fallback
		t.title = String(cell.name || '');
		t.style.background = 'none';
		t.style.border = '0';
		t.style.width = t.style.height = '64px';
		t.style.padding = '0';
		t.textContent = '';
		t.appendChild(img);
		if (info.landed === i) { // BF-ARCADE-CURSOR-LANDED: the real sprite
			t.style.outline = 'none';
			t.style.position = 'relative';
			const landedCursor = document.createElement('img');
			landedCursor.src = '/sprites/bfarcade/35.png';
			landedCursor.alt = '';
			landedCursor.style.cssText = 'position:absolute;left:-8px;top:-8px;width:80px;height:80px;image-rendering:pixelated;pointer-events:none;z-index:1';
			t.appendChild(landedCursor);
		}
	});
}

class PageRoom extends PSRoom {
	override readonly classType: string = 'html';
	readonly page?: string = this.id.split("-")[1];
	override readonly canConnect = true;

	loading = true;
	htmlData?: string;

	setHTMLData = (htmlData?: string) => {
		this.loading = false;
		this.htmlData = htmlData;
		this.update(null);
	};

	constructor(options: RoomOptions) {
		super(options);
		this.connect();
		this.title = this.id.split('-')[1];
	}
	override connect() {
		if (!this.connected && !PagePanel.clientRooms.hasOwnProperty(this.id.split('-')[1])) {
			PS.send(`/join ${this.id}`);
			this.connected = true;
			this.connectWhenLoggedIn = false;
		}
	}
}

function PageLadderHelp() {
	return <div class="ladder pad">
		<p>
			<button class="button" data-href="/ladder" data-target="replace">
				<i class="fa fa-chevron-left" aria-hidden></i> Format List
			</button>
		</p>
		<h3>How the ladder works</h3>
		<p>
			Our ladder displays three ratings: Elo, GXE, and Glicko-1.
		</p>
		<p>
			<strong>Elo</strong> is the main ladder rating. It's a pretty
			normal ladder rating: goes up when you win and down when you
			lose.
		</p>
		<p>
			<strong>GXE</strong> (Glicko X-Act Estimate) is an estimate of
			your win chance against an average ladder player.
		</p>
		<p>
			<strong>Glicko-1</strong> is {}
			<a href="https://en.wikipedia.org/wiki/Glicko_rating_system">another rating system</a>.
			It has rating and deviation values.
		</p>
		<p>
			<strong>COIL</strong> (Converging Order Invariant Ladder) is
			used for suspect tests. The more games you play, the closer
			it will get to your GXE &times; 4000. How fast it reaches
			GXE &times; 4000 depends on {}
			<a href="https://www.smogon.com/forums/threads/reintroducing-coil.3747719/" target="_blank">a custom B value</a> {}
			which is different for each suspect test.
		</p>
		<p>
			Note that win/loss should not be used to estimate skill, since
			who you play against is much more important than how many times
			you win or lose. Our other stats like Elo and GXE are much better
			for estimating skill.
		</p>
	</div>;
}

class PagePanel extends PSRoomPanel<PageRoom> {
	static readonly id = 'html';
	static readonly routes = ['view-*'];
	static readonly Model = PageRoom;
	static clientRooms: { [key: string]: JSX.Element } = { 'ladderhelp': <PageLadderHelp /> };

	/**
	 * @return true to prevent line from being sent to server
	 */
	override receiveLine(args: Args) {
		const { room } = this.props;
		switch (args[0]) {
		case 'title':
			room.title = args[1];
			PS.update();
			return true;
		case 'tempnotify': {
			const [, id, title, body] = args;
			room.notify({ title, body, id });
			return true;
		}
		case 'tempnotifyoff': {
			const [, id] = args;
			room.dismissNotification(id);
			return true;
		}
		case 'selectorhtml':
			const pageHTMLContainer = this.base!.querySelector('.page-html-container');
			const selectedElement = pageHTMLContainer?.querySelector(args[1]);
			if (!selectedElement) return;
			selectedElement.innerHTML = BattleLog.sanitizeHTML(args.slice(2).join('|'));
			room.subtleNotify();
			return true;
		case 'noinit':
			if (args[1] === 'namerequired') {
				room.setHTMLData(args[2]);
			}
			return true;
		case 'pagehtml':
			room.setHTMLData(args[1]);
			room.subtleNotify();
			return true;
		}
	}
	override render() {
		const { room } = this.props;
		let renderPage;
		if (room.page !== undefined && PagePanel.clientRooms[room.page]) {
			renderPage = PagePanel.clientRooms[room.page];
		} else {
			if (room.loading) {
				renderPage = <p>Loading...</p>;
			} else {
				renderPage = <div class="page-html-container">
					<SanitizedHTML>{room.htmlData || ''}</SanitizedHTML>
				</div>;
			}
		}
		return <PSPanelWrapper room={room}>
			{renderPage}
		</PSPanelWrapper>;
	}
}

PS.addRoomType(PagePanel);
